import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { MarketingBrand } from '../../types';
import { api, CreativeBudgetRecord, CreativeEvent, CreativeSessionState, CreativeSessionSummary } from '../../services/api';
import { CreativeTimeline } from './creative/CreativeTimeline';
import { ReferenceLibraryView } from './creative/ReferenceLibraryView';
import { DeliverableViewer } from './creative/DeliverableViewer';
import { MediaOverlay } from './creative/MediaOverlay';
import { BrandMemoryView } from './creative/BrandMemoryView';
import type { IdeaItem } from './creative/IdeasCard';
import { ActivityIcon, formatCredits, relativeTime, STATUS_META } from './creative/creativeUi';

// Criar Conteúdo IA: tela do agente Diretor Criativo.
//
// O agente roda no backend. Esta tela só envia mensagens/decisões e lê a linha do tempo por
// polling (rápido enquanto ele trabalha). Fechar a janela não interrompe nada: ao voltar, a
// sessão está no ponto em que o agente chegou. Ver docs/DIRETOR_CRIATIVO.md.

const ACTIVE_KEY = 'criarConteudoIA.activeSession';
const SUGGESTIONS = [
  'O que eu devo postar esta semana? Veja o que mais funcionou no meu perfil.',
  'Analise meus perfis de inspiração e traga ideias com o que está dando resultado.',
  'Quero um carrossel explicando o principal diferencial da marca.',
  'Crie um post estático para divulgar nosso produto mais vendido.',
  'Planeje um reel de 20 segundos com avatar apresentando a oferta.',
];

const MAX_ATTACHMENTS = 8;
// Mesmo teto do backend (creativeAttachments.ts): vídeo/áudio 500 MB, imagem/documento 40 MB.
const MAX_MEDIA_MB = 500;
const MAX_ATTACHMENT_MB = 40;
const maxMbFor = (f: File) => (/^(video|audio)\//.test(f.type) || /\.(mp4|mov|webm|m4v|mp3|wav|m4a|aac|ogg|opus)$/i.test(f.name) ? MAX_MEDIA_MB : MAX_ATTACHMENT_MB);
const ATTACHMENT_ACCEPT = 'image/*,video/*,audio/*,.pdf,.docx,.xlsx,.xls,.csv,.txt,.md,.json,.html,.srt,.vtt';

function AttachmentChip({ file, onRemove }: { file: File; onRemove: () => void }) {
  const isImage = file.type.startsWith('image/');
  const preview = useMemo(() => (isImage ? URL.createObjectURL(file) : null), [file, isImage]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const kind = isImage ? 'Imagem' : file.type.startsWith('video/') ? 'Vídeo' : file.type.startsWith('audio/') ? 'Áudio' : 'Documento';
  return (
    <div className="group relative flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 py-1.5 pl-1.5 pr-7 text-xs text-slate-600">
      {preview
        ? <img src={preview} alt="" className="h-10 w-10 rounded-lg object-cover" />
        : <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-[10px] font-bold uppercase text-slate-400">{file.name.split('.').pop()?.slice(0, 4)}</span>}
      <span className="flex max-w-[160px] flex-col">
        <span className="truncate font-medium text-slate-700">{file.name}</span>
        <span className="text-[10px] text-slate-400">{kind} · {file.size >= 1048576 ? `${(file.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(file.size / 1024))} KB`}</span>
      </span>
      <button type="button" onClick={onRemove} title="Remover" className="absolute right-1.5 top-1.5 rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700">
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </div>
  );
}

function readActive(): string | null {
  try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; }
}
function writeActive(id: string | null) {
  try { if (id) localStorage.setItem(ACTIVE_KEY, id); else localStorage.removeItem(ACTIVE_KEY); } catch { /* sem storage */ }
}

// `initialSessionId`: abre direto numa conversa (ex.: aberta pelo Agente do Seller).
export function CreateContentAIView({ brand, initialSessionId }: { brand: MarketingBrand | null; initialSessionId?: string | null }) {
  const [sessions, setSessions] = useState<CreativeSessionSummary[]>([]);
  const [activeId, setActiveId] = useState<string | null>(initialSessionId || readActive());
  const [state, setState] = useState<Omit<CreativeSessionState, 'events'> | null>(null);
  const [events, setEvents] = useState<Map<number, CreativeEvent>>(new Map());
  const [input, setInput] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  // Agente da próxima mensagem: automático (o backend roteia) ou fixado pelo usuário.
  const [agentPick, setAgentPick] = useState<'auto' | 'ideacao' | 'producao'>('auto');
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const [sending, setSending] = useState(false);
  // Envio de anexos: bytes enviados/total (null = sem envio em andamento).
  const [upload, setUpload] = useState<{ loaded: number; total: number } | null>(null);
  const uploadAbort = useRef<AbortController | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [showSessions, setShowSessions] = useState(false);
  const [view, setView] = useState<'chat' | 'library' | 'memory'>('chat');
  const openMemory = useCallback(() => setView('memory'), []);
  const [viewer, setViewer] = useState<{ id: string; index: number } | null>(null);
  const cursorRef = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const [atBottom, setAtBottom] = useState(true);
  // Paginação: a conversa abre só com os eventos mais recentes; os antigos vêm sob demanda.
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const loadSessions = useCallback(async () => {
    try {
      const res = await api.listCreativeSessions();
      setSessions(res.data || []);
      return res.data || [];
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao carregar conversas');
      return [];
    }
  }, []);

  // Busca incremental: só eventos novos ou atualizados desde o último retorno.
  const fetchState = useCallback(async (sessionId: string, full = false) => {
    const since = full ? null : cursorRef.current;
    const res = await api.getCreativeSession(sessionId, since);
    const data = res.data;
    // Margem de 3 s contra relógios desalinhados; duplicatas são resolvidas pelo id.
    cursorRef.current = new Date(new Date(data.serverTime).getTime() - 3000).toISOString();
    setEvents(prev => {
      const next = full ? new Map<number, CreativeEvent>() : new Map(prev);
      for (const ev of data.events) next.set(ev.id, ev);
      return next;
    });
    if (full) setHasOlder(!!data.hasMore);
    const { events: _ignored, hasMore: _hm, ...rest } = data;
    setState(rest);
    setSessions(prev => prev.map(s => (s.id === sessionId ? { ...s, status: data.session.status, title: data.session.title, creditsSpent: data.session.creditsSpent, updatedAt: data.session.updatedAt } : s)));
    return data;
  }, []);

  useEffect(() => {
    loadSessions().then(list => {
      if (activeId && !list.some(s => s.id === activeId)) {
        setActiveId(null);
        writeActive(null);
      }
    });
  }, []);

  // Troca de sessão: carga completa.
  useEffect(() => {
    cursorRef.current = null;
    setEvents(new Map());
    setState(null);
    setHasOlder(false);
    stickToBottom.current = true;
    setAtBottom(true);
    if (!activeId) return;
    writeActive(activeId);
    fetchState(activeId, true).catch((e: any) => toast.error(e?.message || 'Erro ao carregar conversa'));
  }, [activeId, fetchState]);

  // Polling: rápido enquanto o agente trabalha, lento no resto (pega mudanças de outra aba).
  // Aba/app em segundo plano não consulta (bateria e dados no celular); ao voltar, consulta já.
  const status = state?.session.status;
  useEffect(() => {
    if (!activeId) return;
    const interval = status === 'running' || status === 'queued' ? 1500 : 6000;
    const tick = () => { if (!document.hidden) fetchState(activeId).catch(() => { /* tenta no próximo ciclo */ }); };
    const t = setInterval(tick, interval);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
  }, [activeId, status, fetchState]);

  const orderedEvents = useMemo(() => Array.from(events.values()).sort((a, b) => a.id - b.id), [events]);

  // Fica PRESO no fim enquanto o usuário estiver no fim. Antes rolava uma vez só, e as imagens que
  // carregavam depois empurravam o conteúdo: no celular a conversa abria no meio e era preciso
  // descer na mão. O ResizeObserver acompanha qualquer mudança de altura (imagem, vídeo, card).
  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);
  useEffect(() => {
    if (stickToBottom.current) scrollToBottom();
  }, [orderedEvents, scrollToBottom]);
  useEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => { if (stickToBottom.current) scrollToBottom(); });
    ro.observe(content);
    return () => ro.disconnect();
  }, [activeId, view, scrollToBottom]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    stickToBottom.current = near;
    if (near !== atBottom) setAtBottom(near);
  };

  // Mensagens anteriores: mantém o ponto de leitura (sem pular para o topo).
  const loadOlder = async () => {
    if (!activeId || loadingOlder) return;
    const oldest = orderedEvents[0]?.id;
    if (!oldest) return;
    const el = scrollRef.current;
    const prevHeight = el?.scrollHeight || 0;
    const prevTop = el?.scrollTop || 0;
    setLoadingOlder(true);
    stickToBottom.current = false;
    try {
      const res = await api.getCreativeSession(activeId, null, { before: oldest });
      setEvents(prev => {
        const next = new Map(prev);
        for (const ev of res.data.events) if (!next.has(ev.id)) next.set(ev.id, ev);
        return next;
      });
      setHasOlder(!!res.data.hasMore);
      requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight - prevHeight + prevTop; });
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao carregar mensagens anteriores');
    } finally {
      setLoadingOlder(false);
    }
  };

  const openDeliverable = useCallback((id: string, index: number) => setViewer({ id, index }), []);

  const newSession = async () => {
    try {
      const res = await api.createCreativeSession(brand?.id || null);
      setSessions(prev => [res.data, ...prev]);
      setActiveId(res.data.id);
      setShowSessions(false);
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao criar conversa');
    }
  };

  const addFiles = (list: FileList | File[] | null) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    const tooBig = incoming.filter(f => f.size > maxMbFor(f) * 1024 * 1024);
    if (tooBig.length) toast.error(`Acima do limite (vídeo/áudio ${MAX_MEDIA_MB} MB, imagem/documento ${MAX_ATTACHMENT_MB} MB): ${tooBig.map(f => f.name).join(', ')}`);
    setFiles(prev => {
      const next = [...prev, ...incoming.filter(f => f.size <= maxMbFor(f) * 1024 * 1024)];
      if (next.length > MAX_ATTACHMENTS) toast.error(`No máximo ${MAX_ATTACHMENTS} anexos por mensagem.`);
      return next.slice(0, MAX_ATTACHMENTS);
    });
  };

  const send = async (textArg?: string, agentOverride?: 'ideacao' | 'producao') => {
    const text = (textArg ?? input).trim();
    const outgoing = textArg !== undefined ? [] : files;
    if ((!text && !outgoing.length) || sending) return;
    setSending(true);
    try {
      let sessionId = activeId;
      if (!sessionId) {
        const res = await api.createCreativeSession(brand?.id || null);
        setSessions(prev => [res.data, ...prev]);
        sessionId = res.data.id;
        setActiveId(sessionId);
      }
      if (outgoing.length) {
        uploadAbort.current = new AbortController();
        setUpload({ loaded: 0, total: outgoing.reduce((s, f) => s + f.size, 0) });
      }
      await api.sendCreativeMessage(sessionId, text, outgoing, agentOverride || (agentPick === 'auto' ? undefined : agentPick), {
        onProgress: (loaded, total) => setUpload({ loaded, total }),
        signal: uploadAbort.current?.signal,
      });
      setInput('');
      if (outgoing.length) setFiles([]);
      stickToBottom.current = true;
      await fetchState(sessionId, !activeId);
      loadSessions();
    } catch (e: any) {
      if (e?.aborted) toast('Envio cancelado. Os anexos continuam aqui.');
      else toast.error(e?.message || 'Erro ao enviar');
    } finally {
      setSending(false);
      setUpload(null);
      uploadAbort.current = null;
    }
  };

  const decide = async (budgetId: string, decision: 'approve' | 'reject', reason?: string, option?: 'recomendado' | 'economico') => {
    if (!activeId) return;
    try {
      await api.decideCreativeBudget(activeId, budgetId, decision, reason, option);
      toast.success(decision === 'approve' ? (option === 'economico' ? 'Versão econômica aprovada. O agente vai produzir.' : 'Orçamento aprovado. O agente vai produzir.') : 'Orçamento recusado.');
      await fetchState(activeId);
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao decidir o orçamento');
    }
  };

  const decidePublication = async (publicationId: string, decision: 'approve' | 'reject', extra: { caption?: string; tiktokCaption?: string; reason?: string; aiLabel?: boolean } = {}) => {
    if (!activeId) return;
    try {
      await api.decideCreativePublication(activeId, publicationId, decision, extra);
      toast.success(decision === 'approve' ? 'Aprovado. Enviando ao Zernio…' : 'Publicação recusada.');
      await fetchState(activeId);
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao decidir a publicação');
    }
  };

  // A mensagem leva o título, as aberturas e o marcador [ideia:<id>]: o backend anexa o texto
  // COMPLETO da ideia a partir do registro salvo (o card pode estar resumido). Ideias sem id
  // (quadros antigos) ainda mandam o brief do card.
  const produceIdea = async (idea: IdeaItem) => {
    const msg = idea.id
      ? [
        `Produza a ideia "${idea.title}" (${idea.format}). [ideia:${idea.id}]`,
        `Abertura A: ${idea.hook}${idea.onScreen ? ` · Na tela: ${idea.onScreen}` : ''}`,
        idea.openingB?.hook ? `Abertura B: ${idea.openingB.hook}${idea.openingB.onScreen ? ` · Na tela: ${idea.openingB.onScreen}` : ''}` : '',
      ].filter(Boolean).join('\n')
      : [
        `Produza a ideia "${idea.title}" (${idea.format}).`,
        `Gancho: ${idea.hook}${idea.onScreen ? ` · Na tela: ${idea.onScreen}` : ''}`,
        idea.angle ? `Ângulo: ${idea.angle}` : '',
        idea.evidence ? `Evidência: ${idea.evidence}` : '',
        idea.brief ? `Brief: ${idea.brief}` : '',
      ].filter(Boolean).join('\n');
    await send(msg, 'producao');
  };

  const stop = async () => {
    if (!activeId) return;
    try { await api.stopCreativeSession(activeId); toast('O agente vai parar no próximo passo.'); } catch (e: any) { toast.error(e?.message || 'Erro ao parar'); }
  };
  const resume = async () => {
    if (!activeId) return;
    try { await api.resumeCreativeSession(activeId); await fetchState(activeId); } catch (e: any) { toast.error(e?.message || 'Erro ao retomar'); }
  };
  const removeSession = async (id: string) => {
    if (!window.confirm('Apagar esta conversa?')) return;
    try {
      await api.deleteCreativeSession(id);
      setSessions(prev => prev.filter(s => s.id !== id));
      if (id === activeId) setActiveId(null);
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao apagar');
    }
  };

  // Resumo lateral.
  const activities = orderedEvents.filter(e => e.type === 'activity');
  const currentActivity = [...activities].reverse().find(a => a.payload?.status === 'running');
  const images = activities.flatMap(a => (Array.isArray(a.payload?.data?.images) ? a.payload.data.images : []));
  const budgets: CreativeBudgetRecord[] = state?.budgets || [];
  const deliverables = state?.deliverables || [];
  const viewerDeliverable = viewer ? deliverables.find(d => d.id === viewer.id) : null;
  const approvedBudgets = budgets.filter(b => b.status === 'approved');
  const pendingBudget = budgets.find(b => b.status === 'proposed');
  const plan = [...orderedEvents].reverse().find(e => e.type === 'plan');
  const meta = STATUS_META[status || 'idle'] || STATUS_META.idle;
  // Na fila (container do agente ainda não pegou) conta como trabalhando para a tela.
  const running = status === 'running' || status === 'queued';
  const inputDisabled = running || sending;

  return (
    // min-h só no desktop: no celular os 560px passavam da tela e criavam rolagem dupla (página +
    // conversa), que é o que deixava o arrasto travado.
    <div className="relative flex h-[calc(100dvh-7.5rem)] overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm md:min-h-[560px]">
      {showSessions && <div className="absolute inset-0 z-10 bg-black/30 md:hidden" onClick={() => setShowSessions(false)} />}
      {/* Conversas */}
      <aside className={`${showSessions ? 'flex' : 'hidden'} absolute inset-y-0 left-0 z-20 w-72 flex-col border-r border-slate-200 bg-white md:static md:flex md:w-64`}>
        <div className="flex items-center justify-between gap-2 border-b border-slate-100 p-3">
          <p className="text-sm font-bold text-slate-900">Conversas</p>
          <button type="button" onClick={newSession} className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800">+ Nova</button>
        </div>
        <div className="flex-1 space-y-1 overflow-y-auto p-2">
          {sessions.length === 0 && <p className="px-2 py-6 text-center text-xs text-slate-400">Nenhuma conversa ainda.</p>}
          {sessions.map(s => {
            const m = STATUS_META[s.status] || STATUS_META.idle;
            return (
              <div key={s.id} className={`group flex items-start gap-2 rounded-xl px-3 py-2.5 ${s.id === activeId ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}>
                <button type="button" onClick={() => { setActiveId(s.id); setShowSessions(false); }} className="min-w-0 flex-1 text-left">
                  <p className={`truncate text-sm font-semibold ${s.id === activeId ? 'text-indigo-900' : 'text-slate-700'}`}>{s.title}</p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-400">
                    <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
                    {m.label} · {relativeTime(s.updatedAt)} · {formatCredits(s.creditsSpent)} cr
                  </p>
                </button>
                <button type="button" onClick={() => removeSession(s.id)} title="Apagar" className="hidden rounded p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-500 group-hover:block">
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" /></svg>
                </button>
              </div>
            );
          })}
        </div>
      </aside>

      {/* Conversa ativa ou Biblioteca */}
      <section className="relative flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-1 overflow-x-auto border-b border-slate-200 bg-white px-3 pt-2">
          {([['chat', 'Conversa'], ['memory', 'Memória da marca'], ['library', 'Biblioteca de Referências']] as const).map(([id, label]) => (
            <button key={id} type="button" onClick={() => setView(id)} className={`-mb-px shrink-0 whitespace-nowrap rounded-t-lg border-b-2 px-3 py-2 text-xs font-bold ${view === id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>{label}</button>
          ))}
        </div>
        {view === 'library' ? (
          <ReferenceLibraryView onUse={text => { setView('chat'); setInput(text); }} />
        ) : view === 'memory' ? (
          <BrandMemoryView brandId={state?.session.brandId || brand?.id || null} brandName={brand?.name} />
        ) : (<>
        <header className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
          <button type="button" onClick={() => setShowSessions(v => !v)} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 md:hidden" aria-label="Conversas">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-slate-900">{state?.session.title || 'Diretor Criativo'}</p>
            <p className="text-[11px] text-slate-400">
              {brand ? `Marca: ${brand.name}` : 'Sem brand configurada'}
              {state?.session.activeAgent && <span className={`ml-2 rounded-full px-2 py-0.5 font-bold ${state.session.activeAgent === 'ideacao' ? 'bg-violet-50 text-violet-700' : 'bg-indigo-50 text-indigo-700'}`}>{state.session.activeAgent === 'ideacao' ? 'Ideação' : 'Produção'}</span>}
            </p>
          </div>
          {state && (
            <>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${meta.cls}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />{meta.label}
              </span>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600" title="Créditos gastos nesta conversa (tokens + ferramentas)">
                Total da sessão: {formatCredits(state.session.creditsSpent, 3)} cr
              </span>
              <span className="hidden rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 sm:inline" title="Saldo do tenant">
                Saldo: {formatCredits(state.balance)} cr
              </span>
              {running && <button type="button" onClick={stop} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Parar</button>}
              {(status === 'interrupted' || status === 'error') && <button type="button" onClick={resume} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">Retomar</button>}
            </>
          )}
        </header>

        <div className="relative min-h-0 flex-1">
        <div ref={scrollRef} onScroll={onScroll} className="h-full overflow-y-auto overscroll-contain px-3 py-4 sm:px-6 sm:py-5" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div ref={contentRef}>
          {!activeId || orderedEvents.length === 0 ? (
            <div className="mx-auto flex max-w-xl flex-col items-center pt-10 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 text-lg font-black text-white shadow-lg">DC</span>
              <h2 className="mt-4 text-xl font-black text-slate-900">Diretor criativo com IA</h2>
              <p className="mt-2 text-sm text-slate-500">
                Ele consulta a sua marca, planeja o conteúdo, mostra o orçamento em créditos antes de gerar e só produz depois da sua aprovação.
                Pode fechar a janela: o trabalho continua no servidor.
              </p>
              {!brand && <p className="mt-4 rounded-xl bg-amber-50 px-4 py-2 text-sm text-amber-800">Configure a brand em Brand Configs para o agente ter o que consultar.</p>}
              <div className="mt-6 grid w-full gap-2">
                {SUGGESTIONS.map(s => (
                  <button key={s} type="button" disabled={inputDisabled} onClick={() => send(s)} className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-700 shadow-sm hover:border-indigo-300 hover:bg-indigo-50/40 disabled:opacity-50">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mx-auto max-w-3xl">
              {hasOlder && (
                <div className="mb-4 flex justify-center">
                  <button type="button" onClick={loadOlder} disabled={loadingOlder} className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold text-slate-600 shadow-sm hover:bg-slate-50 disabled:opacity-50">
                    {loadingOlder ? 'Carregando…' : 'Carregar mensagens anteriores'}
                  </button>
                </div>
              )}
              <CreativeTimeline events={orderedEvents} budgets={budgets} status={status || 'idle'} onDecide={decide} onImage={setLightbox} onOpenDeliverable={openDeliverable} onDecidePublication={decidePublication} onProduceIdea={produceIdea} onOpenMemory={openMemory} />
              {running && !currentActivity && (
                <div className="mt-4 flex items-center gap-2 text-sm text-slate-400">
                  <span className="flex gap-1"><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-400" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-400 [animation-delay:120ms]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-400 [animation-delay:240ms]" /></span>
                  Pensando…
                </div>
              )}
            </div>
          )}
          </div>
        </div>
        {!atBottom && activeId && orderedEvents.length > 0 && (
          <button type="button" onClick={() => { stickToBottom.current = true; setAtBottom(true); scrollToBottom(true); }} aria-label="Ir para o fim da conversa"
            className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-lg hover:bg-slate-50">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
          </button>
        )}
        </div>

        <div
          className={`border-t bg-white p-2 sm:p-3 ${dragging ? 'border-indigo-400 bg-indigo-50/60' : 'border-slate-200'}`}
          style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
          onDragOver={e => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragging(true); } }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { if (e.dataTransfer.files?.length) { e.preventDefault(); addFiles(e.dataTransfer.files); } setDragging(false); }}
        >
          <div className="mx-auto mb-2 flex max-w-3xl items-center gap-1 text-[11px]">
            <span className="mr-1 font-semibold text-slate-400">Agente:</span>
            {([['auto', 'Automático', 'Escolhe pelo que você pede'], ['ideacao', 'Ideação', 'Pesquisa o que funciona e traz pautas com evidência'], ['producao', 'Produção', 'Cria a peça (carrossel, post, reel)']] as const).map(([id, label, hint]) => (
              <button key={id} type="button" title={hint} onClick={() => setAgentPick(id)}
                className={`rounded-full px-2.5 py-1 font-semibold ${agentPick === id ? (id === 'ideacao' ? 'bg-violet-600 text-white' : id === 'producao' ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-white') : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                {label}
              </button>
            ))}
          </div>
          {upload && (() => {
            const pct = upload.total > 0 ? Math.min(100, Math.round((upload.loaded / upload.total) * 100)) : 0;
            const mb = (n: number) => (n / 1048576).toFixed(n >= 10 * 1048576 ? 0 : 1);
            const done = pct >= 100;
            return (
              <div className="mx-auto mb-2 max-w-3xl rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2" role="status" aria-live="polite">
                <div className="mb-1 flex items-center justify-between gap-3 text-xs text-indigo-900">
                  <span className="font-medium">
                    {done ? 'Arquivos enviados. Preparando para o agente…' : `Enviando ${files.length === 1 ? 'anexo' : `${files.length} anexos`}… ${pct}%`}
                  </span>
                  <span className="flex items-center gap-3 text-indigo-700">
                    {!done && <span>{mb(upload.loaded)} de {mb(upload.total)} MB</span>}
                    {!done && (
                      <button type="button" onClick={() => uploadAbort.current?.abort()} className="font-semibold hover:underline">Cancelar</button>
                    )}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-indigo-100">
                  <div
                    className={`h-full rounded-full bg-indigo-600 transition-[width] duration-200 ${done ? 'animate-pulse' : ''}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })()}
          {files.length > 0 && (
            <div className="mx-auto mb-2 flex max-w-3xl flex-wrap gap-2">
              {files.map((f, i) => <AttachmentChip key={`${f.name}-${i}`} file={f} onRemove={() => setFiles(prev => prev.filter((_, j) => j !== i))} />)}
            </div>
          )}
          <div className="mx-auto flex max-w-3xl items-end gap-2">
            <input ref={fileInput} type="file" multiple accept={ATTACHMENT_ACCEPT} className="hidden" onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={inputDisabled}
              title="Anexar imagens, vídeos, áudios ou documentos (PDF, Word, planilha, texto)"
              className="flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-xl border border-slate-300 text-slate-500 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-40"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
            </button>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onPaste={e => { const pasted = Array.from(e.clipboardData?.files || []); if (pasted.length) { e.preventDefault(); addFiles(pasted); } }}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={2}
              disabled={inputDisabled}
              placeholder={running ? 'O agente está trabalhando… você pode fechar a janela.' : status === 'waiting_approval' ? 'Aprove o orçamento acima ou peça um ajuste…' : 'Peça um conteúdo, mande um link ou anexe imagens e documentos…'}
              // text-base no celular: o iOS dá zoom na página ao focar campo com fonte < 16px.
              className="min-h-[48px] min-w-0 flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2.5 text-base outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50 sm:text-sm"
            />
            <button type="button" onClick={() => send()} disabled={inputDisabled || (!input.trim() && !files.length)} className="h-[48px] shrink-0 rounded-xl bg-indigo-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-40 sm:px-5">
              {upload ? 'Enviando…' : sending ? '…' : 'Enviar'}
            </button>
          </div>
        </div>
        </>)}
      </section>

      {/* Resumo da sessão */}
      {state && view === 'chat' && (
        <aside className="hidden w-72 shrink-0 flex-col gap-4 overflow-y-auto border-l border-slate-200 bg-white p-4 xl:flex">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Agora</p>
            {currentActivity ? (
              <div className="mt-2 flex items-center gap-3 rounded-xl bg-indigo-50 p-3">
                <ActivityIcon kind={currentActivity.payload.kind} running />
                <p className="text-sm font-semibold text-indigo-900">{currentActivity.payload.label}</p>
              </div>
            ) : (
              <p className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${meta.cls}`}><span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />{meta.label}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-slate-50 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total da sessão</p>
              <p className="mt-1 text-lg font-black text-slate-900">{formatCredits(state.session.creditsSpent, 3)}</p>
              <p className="text-[10px] text-slate-400">créditos · tokens e ferramentas</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Etapas</p>
              <p className="mt-1 text-lg font-black text-slate-900">{activities.filter(a => a.payload?.status === 'done').length}</p>
              <p className="text-[10px] text-slate-400">concluídas</p>
            </div>
          </div>

          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Orçamentos</p>
            {budgets.length === 0 && <p className="mt-2 text-xs text-slate-400">Nenhum ainda.</p>}
            {pendingBudget && (
              <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">Aguardando aprovação: {formatCredits(pendingBudget.totalCredits, 3)} cr</p>
            )}
            {approvedBudgets.map(b => {
              const pct = b.totalCredits > 0 ? Math.min(100, (b.consumedCredits / b.totalCredits) * 100) : 0;
              return (
                <div key={b.id} className="mt-2">
                  <div className="flex justify-between text-xs text-slate-600">
                    <span>Aprovado</span>
                    <span className="font-semibold">{formatCredits(b.consumedCredits, 3)} / {formatCredits(b.totalCredits, 3)}</span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>

          {plan && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Plano atual</p>
              <p className="mt-2 text-sm font-semibold text-slate-800">{plan.payload.title}</p>
              <p className="text-xs text-slate-500">{(plan.payload.steps || []).length} etapa(s)</p>
            </div>
          )}

          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Entregas</p>
            {deliverables.map(d => {
              const cover = d.assets?.[d.coverIndex]?.url || d.assets?.[0]?.url;
              const errors = (d.assets || []).reduce((n, a) => n + (a.issues || []).filter(x => x.severity === 'error').length, 0);
              return (
                <button key={d.id} type="button" onClick={() => setViewer({ id: d.id, index: d.coverIndex })} className="mt-2 flex w-full items-center gap-3 rounded-xl border border-slate-200 p-2 text-left hover:border-indigo-300 hover:bg-indigo-50/40">
                  {cover ? <img src={cover} alt="" className="h-16 w-12 rounded-md object-cover" /> : <div className="h-16 w-12 rounded-md bg-slate-100" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-800">{d.title}</p>
                    <p className="text-[11px] text-slate-500">{d.kind === 'carousel' ? 'Carrossel' : d.kind === 'story' ? 'Story' : 'Post'} · {(d.assets || []).length} slide(s)</p>
                    <p className={`text-[11px] font-semibold ${d.status === 'scheduled' || d.status === 'published' ? 'text-green-700' : errors ? 'text-rose-600' : 'text-emerald-600'}`}>
                      {d.status === 'scheduled' ? 'Agendado' : d.status === 'published' ? 'Publicado' : errors ? `${errors} erro(s) na checagem` : 'Pronto para publicar'}
                    </p>
                  </div>
                </button>
              );
            })}
            {images.length === 0 && deliverables.length === 0 ? <p className="mt-2 text-xs text-slate-400">Nada gerado ainda.</p> : images.length === 0 ? null : (
              <div className="mt-2 grid grid-cols-2 gap-2">
                {images.map((img: any) => (
                  <button key={img.url} type="button" onClick={() => setLightbox(img.url)} className="overflow-hidden rounded-lg border border-slate-200">
                    <img src={img.url} alt="Entrega" className="aspect-[4/5] w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>
      )}

      {viewerDeliverable && viewer && (
        <DeliverableViewer key={`${viewer.id}-${viewer.index}`} deliverable={viewerDeliverable} startIndex={viewer.index} onClose={() => setViewer(null)} />
      )}

      {lightbox && (
        <MediaOverlay onClose={() => setLightbox(null)} label="Imagem">
          <img src={lightbox} alt="Imagem gerada" decoding="async" onClick={e => e.stopPropagation()} className="max-h-[85dvh] max-w-[94vw] rounded-xl object-contain shadow-2xl" />
          <a href={lightbox} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
            className="absolute left-1/2 -translate-x-1/2 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-slate-800 shadow-lg"
            style={{ bottom: 'max(1rem, env(safe-area-inset-bottom))' }}>Abrir original</a>
        </MediaOverlay>
      )}
    </div>
  );
}
